import React, { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@mebi/ui";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  showDetails?: boolean;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ error, errorInfo });

    // Log error to console in development
    if (process.env.NODE_ENV === "development") {
      console.error("ErrorBoundary caught an error:", error, errorInfo);
    }

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="flex flex-col items-center justify-center min-h-[200px] p-6 bg-neutral-50 rounded-lg border border-neutral-200">
          <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>

          <h3 className="text-lg font-semibold text-neutral-900 mb-2 text-center">
            Có lỗi xảy ra
          </h3>

          <p className="text-neutral-600 text-center mb-4 max-w-sm">
            Không thể tải nội dung này. Vui lòng thử lại hoặc liên hệ hỗ trợ nếu
            vấn đề vẫn tiếp tục.
          </p>

          {this.props.showDetails && this.state.error && (
            <details className="w-full max-w-md mb-4">
              <summary className="text-sm text-neutral-500 cursor-pointer hover:text-neutral-700">
                Chi tiết lỗi
              </summary>
              <div className="mt-2 p-3 bg-neutral-100 rounded text-xs text-neutral-700 font-mono overflow-auto max-h-32">
                <div className="font-semibold mb-1">Error:</div>
                <div className="mb-2">{this.state.error.message}</div>
                {this.state.errorInfo && (
                  <>
                    <div className="font-semibold mb-1">Stack:</div>
                    <div>{this.state.errorInfo.componentStack}</div>
                  </>
                )}
              </div>
            </details>
          )}

          <Button
            onClick={this.handleRetry}
            variant="primary"
            size="sm"
            className="flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Thử lại
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Functional component wrapper for easier usage
export const ErrorBoundaryWrapper: React.FC<Props> = React.memo((props) => (
  <ErrorBoundary {...props} />
));

// Specialized error boundaries for different contexts
export const LazyLoadErrorBoundary: React.FC<{ children: ReactNode }> =
  React.memo(({ children }) => (
    <ErrorBoundary
      fallback={
        <div className="flex items-center justify-center h-32 bg-neutral-100 rounded-lg">
          <div className="text-center">
            <AlertTriangle className="w-6 h-6 text-neutral-400 mx-auto mb-2" />
            <p className="text-sm text-neutral-600">Không thể tải component</p>
          </div>
        </div>
      }
      onError={(error) => {
        console.warn("Lazy loading failed:", error.message);
      }}
    >
      {children}
    </ErrorBoundary>
  ));

export const RouteErrorBoundary: React.FC<{ children: ReactNode }> = React.memo(
  ({ children }) => (
    <ErrorBoundary
      fallback={
        <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <div className="flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mx-auto mb-6">
              <AlertTriangle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-neutral-900 mb-4">
              Trang không thể tải
            </h1>
            <p className="text-neutral-600 mb-6">
              Có lỗi xảy ra khi tải trang này. Vui lòng thử lại hoặc quay về
              trang chủ.
            </p>
            <div className="space-y-3">
              <Button
                onClick={() => globalThis.location.reload()}
                variant="primary"
                className="w-full"
              >
                Tải lại trang
              </Button>
              <Button
                onClick={() => globalThis.history.back()}
                variant="secondary"
                className="w-full"
              >
                Quay lại
              </Button>
            </div>
          </div>
        </div>
      }
      showDetails={process.env.NODE_ENV === "development"}
    >
      {children}
    </ErrorBoundary>
  ),
);

export default ErrorBoundary;
